import React, { useCallback, useContext } from 'react';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from 'react-beautiful-dnd';

import Row from 'components/Common/Row';
import { Item } from 'components/CustomerLaneCommitsBoard/Settings/QuickFilters/QuickFilters';
import FilterPlateForm from 'components/FilterPlateForm';
import InlineDialog from 'components/InlineDialog';
import FilterPlatesProvider from 'contextProviders/FilterPlatesProvider';
import BaseViewsContext from 'contexts/BaseViewsContext';
import FilterPlatesContext from 'contexts/FilterPlatesContext';
import { getDrillDownFieldName } from 'hooks/useDrillDownFieldName';
import usePopup from 'hooks/usePopup';
import Button from 'kingpin/atoms/Button';
import Typography from 'kingpin/atoms/Typography';
import Form from 'kingpin/forms/Form';
import FormContent from 'kingpin/forms/FormContent';
import FormHeader from 'kingpin/forms/FormHeader';
import aguid from 'lib/aguid';

import { FilterBarSettings } from '../filterBarSettings';

// A report's or dashboard's pinned filter fields: pinned, reordered and
// unpinned
const PinnedFiltersSettings = ({
  settings,
  close,
}: {
  settings: FilterBarSettings;
  close: () => void;
}) => {
  const { dataTypes } = useContext(FilterPlatesContext);
  const { baseViews } = useContext(BaseViewsContext);
  const {
    isOpen: isFieldPickerOpen,
    open: openFieldPicker,
    close: closeFieldPicker,
  } = usePopup();
  const { pinnedFilterFields: pins, save } = settings;

  const savePins = useCallback(
    (pinnedFilterFields: ReportPinnedFilters.Field[]) => {
      save({ pinnedFilterFields });
    },
    [save],
  );

  const onFieldPicked = useCallback(
    (field: FleetOps.Field | DateBucketField) => {
      if (pins.some((p) => p.field === field.field)) {
        return;
      }

      savePins([
        ...pins,
        {
          id: aguid(),
          field: field.field,
          fieldType: field.type,
          dataset: field.dataType,
        },
      ]);
    },
    [pins, savePins],
  );

  const removePin = useCallback(
    (pin: ReportPinnedFilters.Field) => {
      savePins(pins.filter((p) => p.id !== pin.id));
    },
    [pins, savePins],
  );

  const onDragEnd = useCallback(
    ({ draggableId, destination }: DropResult) => {
      if (!destination) {
        return;
      }

      const dropped = pins.find((p) => p.id === draggableId);
      if (!dropped) {
        return;
      }

      const others = pins.filter((p) => p.id !== draggableId);
      savePins([
        ...others.slice(0, destination.index),
        dropped,
        ...others.slice(destination.index),
      ]);
    },
    [pins, savePins],
  );

  return (
    <Form>
      <FormHeader
        title={'Pinned Filters'}
        subTitle={
          'These fields always show on the filter bar, ready for viewers to pick values'
        }
        onClose={close}
      />
      <FormContent>
        <div style={{ minHeight: 240 }}>
          {pins.length === 0 && (
            <Typography.Body type="Body 12">
              No fields pinned yet
            </Typography.Body>
          )}
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId={'pinned-filters-drop'}>
              {(provided) => (
                <div ref={provided.innerRef}>
                  {pins.map((pin, index) => (
                    <Draggable draggableId={pin.id} index={index} key={pin.id}>
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                        >
                          <Item>
                            <Typography.Body type="Body 12">
                              {getDrillDownFieldName(pin, baseViews)}
                            </Typography.Body>
                            <Button
                              onClick={() => removePin(pin)}
                              type="Secondary"
                              size="Small"
                              icon="cross"
                              testId={`remove-pinned-filter-${pin.field}`}
                            />
                          </Item>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>
      </FormContent>
      <Row style={{ justifyContent: 'space-between' }}>
        <Button onClick={close} type="Ghost" size="Small" label="Close" />
        <FilterPlatesProvider
          dataTypes={dataTypes}
          fieldRedList={pins.map((p) => p.field)}
        >
          <InlineDialog
            isOpen={isFieldPickerOpen}
            onClose={closeFieldPicker}
            content={
              <FilterPlateForm
                close={closeFieldPicker}
                isScope={false}
                onFieldPicked={onFieldPicked}
              />
            }
          >
            <Button
              type="Primary"
              size="Small"
              onClick={isFieldPickerOpen ? closeFieldPicker : openFieldPicker}
              label="Pin a field"
              icon="add"
              testId="add-pinned-filter"
            />
          </InlineDialog>
        </FilterPlatesProvider>
      </Row>
    </Form>
  );
};

export default PinnedFiltersSettings;
